/**
 * VerificationDayColumn Component
 *
 * Reusable component for each day column in the verification dialog.
 * Replaces 7 identical 130-line day sections with a single component.
 */

import React, { memo } from 'react';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import FormInputText from '../../common/Forms/FormTextField';
import TreatmentTypeDropdown from './TreatmentTypeDropdown';
import { VerifyWeekTypo } from '../../ui/styled';
import { formDateFormat } from '../../../constants';
import { TREATMENT_TYPES, DIALOG_TYPES } from '../utils/verificationConstants';

/**
 * VerificationDayColumn Component
 *
 * @param {Object} props - Component props
 * @param {number} props.dayIndex - Day index (1-7)
 * @param {string} props.dayName - Day name (Monday, Tuesday, etc.)
 * @param {Date} props.date - Date for this day
 * @param {Object} props.control - React Hook Form control
 * @param {Function} props.register - React Hook Form register function
 * @param {Array} props.individualOptions - Individual dropdown options
 * @param {Array} props.groupOptions - Group dropdown options
 * @param {Array} props.dischargeOptions - Discharge dropdown options
 * @param {boolean} props.isDisabled - Whether day is disabled due to prior discharge
 * @param {boolean} props.canDisplayGroup - Whether group treatment is allowed
 * @param {Function} props.onStatusChange - Status change handler
 * @param {Object} props.defaultValue - Default values from form
 * @param {Function} props.setI - Redux setter for individual treatment
 * @param {Function} props.setG - Redux setter for group treatment
 * @param {Function} props.setD - Redux setter for discharge treatment
 * @returns {JSX.Element} VerificationDayColumn component
 */
const VerificationDayColumn = ({
  dayIndex,
  dayName,
  date,
  control,
  register,
  individualOptions,
  groupOptions,
  dischargeOptions,
  isDisabled,
  canDisplayGroup,
  onStatusChange,
  defaultValue,
  setI,
  setG,
  setD,
}) => {
  // Field names for this day
  const individualField = `i${dayIndex}TmtTmtStsId`;
  const groupField = `g${dayIndex}TmtTmtStsId`;
  const dischargeField = `d${dayIndex}TmtTmtStsId`;
  const stopHoursField = `stop${dayIndex}Hours`;

  // Format date display
  const formattedDate = dayIndex === 1
    ? moment(date).format(formDateFormat)
    : moment(date).format(formDateFormat);

  return (
    <Grid item xs={12} sm={1.714}>
      {/* Day header */}
      <VerifyWeekTypo variant='h6'>{dayName}:</VerifyWeekTypo>
      <VerifyWeekTypo variant='h6'>{formattedDate}</VerifyWeekTypo>

      {/* Individual treatment dropdown */}
      <TreatmentTypeDropdown
        name={individualField}
        control={control}
        register={register}
        label='Individual'
        options={individualOptions}
        disabled={isDisabled}
        treatmentType={TREATMENT_TYPES.INDIVIDUAL}
        onChange={(e) => {
          onStatusChange(
            individualField,
            e.target.value,
            defaultValue[individualField],
            setI,
            TREATMENT_TYPES.INDIVIDUAL,
            DIALOG_TYPES.INDIVIDUAL_OR_GROUP
          );
        }}
      />

      {/* Group treatment dropdown (conditional) */}
      {canDisplayGroup === true && (
        <TreatmentTypeDropdown
          name={groupField}
          control={control}
          register={register}
          label='Group'
          options={groupOptions}
          disabled={isDisabled}
          treatmentType={TREATMENT_TYPES.GROUP}
          onChange={(e) => {
            onStatusChange(
              groupField,
              e.target.value,
              defaultValue[groupField],
              setG,
              TREATMENT_TYPES.GROUP,
              DIALOG_TYPES.INDIVIDUAL_OR_GROUP
            );
          }}
        />
      )}

      {/* Discharge dropdown */}
      <TreatmentTypeDropdown
        name={dischargeField}
        control={control}
        register={register}
        label='Discharge'
        options={dischargeOptions}
        disabled={isDisabled}
        treatmentType={TREATMENT_TYPES.DISCHARGE}
        onChange={(e) => {
          onStatusChange(
            dischargeField,
            e.target.value,
            defaultValue[dischargeField],
            setD,
            TREATMENT_TYPES.DISCHARGE,
            DIALOG_TYPES.DISCHARGE
          );
        }}
      />

      {/* STOP Hours (read-only) */}
      <FormInputText
        name={stopHoursField}
        control={control}
        shrink={true}
        label='STOP Hours'
        disabled={true}
      />
    </Grid>
  );
};

export default memo(VerificationDayColumn);
